/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { FormattedDate, FormattedMessage } from 'react-intl'
import { DateTime } from 'luxon'
import { getTransactionLink } from '../../lib/links/transactionLinks'
import Amount from '../transaction/Amount'
import { Accordion, Group, Table, Title } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'

export type CustomerReportPeriodProps = {
  data: TransactionReportPeriod
}

function CustomerReportPeriod (props: CustomerReportPeriodProps) {
  const { data } = props
  return (
    <Accordion.Item value={data.label}>
      <Accordion.Control>
        <Group>
          <Title tt="capitalize" order={2}>
            <FormattedDate
              value={DateTime.fromFormat(data.label, 'yyyy-MM').toJSDate()}
              year="numeric"
              month="long"
            />
          </Title>

          <Group ml="auto">
            <Amount size="lg" fw="bold" value={data.balanceWithTax} colored />
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={100}>
                <FormattedMessage
                  id="date"
                  defaultMessage="Date"
                />
              </Table.Th>
              <Table.Th>
                <FormattedMessage
                  id="label"
                  defaultMessage="Libellé"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="revenues_excluding_taxes"
                  defaultMessage="Revenus HT"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="expenses_excluding_taxes"
                  defaultMessage="Dépenses HT"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="vat"
                  defaultMessage="TVA"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="cps"
                  defaultMessage="CPS"
                />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {data.transactions.map((trans) => (
              <Table.Tr key={trans.id}>
                <Table.Td>
                  <FormattedDate value={trans.dueAt} />
                </Table.Td>
                <Table.Td>
                  <AnchorLink to={getTransactionLink(trans.id, !trans.expense)}>{trans.label}</AnchorLink>
                </Table.Td>
                <Table.Td ta="end">
                  {!trans.expense
                    ? <Amount value={trans.amount} inherit />
                    : null}
                </Table.Td>
                <Table.Td ta="end">
                  {trans.expense
                    ? <Amount value={trans.amount} inherit />
                    : null}
                </Table.Td>
                <Table.Td ta="end">
                  <Amount value={trans.vat} />
                </Table.Td>
                <Table.Td ta="end">
                  <Amount value={trans.cps} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>

          <Table.Tfoot bg="gray.0">
            <Table.Tr>
              <Table.Td colSpan={2} />
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="revenues_excluding_taxes"
                  defaultMessage="Revenus HT"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="expenses_excluding_taxes"
                  defaultMessage="Dépenses HT"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="vat"
                  defaultMessage="TVA"
                />
              </Table.Th>
              <Table.Th
                ta="end"
                w={120}
              >
                <FormattedMessage
                  id="cps"
                  defaultMessage="CPS"
                />
              </Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th colSpan={2}>
                <FormattedMessage
                  id="total"
                  defaultMessage="Total"
                />
              </Table.Th>
              <Table.Td ta="end">
                <Amount inherit value={data.revenues} />
              </Table.Td>
              <Table.Td ta="end">
                <Amount inherit value={data.expenses} />
              </Table.Td>
              <Table.Td ta="end">
                <Amount inherit value={data.vat} />
              </Table.Td>
              <Table.Td ta="end">
                <Amount inherit value={data.cps} />
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th colSpan={2}>
                <FormattedMessage
                  id="balance_excluding_taxes"
                  defaultMessage="Solde HT"
                />
              </Table.Th>
              <Table.Td ta="end">
                <Amount inherit value={data.balance} colored />
              </Table.Td>
              <Table.Td colSpan={3}></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th colSpan={2}>
                <FormattedMessage
                  id="balance_including_taxes"
                  defaultMessage="Solde TTC"
                />
              </Table.Th>
              <Table.Td ta="end">
                <Amount inherit value={data.balanceWithTax} colored />
              </Table.Td>
              <Table.Td colSpan={3}></Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Accordion.Panel>
    </Accordion.Item>
  )
}

export default CustomerReportPeriod
